import * as React from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import {FormControl, MenuItem, Select} from "@mui/material";
import {useEffect, useRef, useState} from "react";
import {LessonHistory} from "../types/stats";
import {theme} from "../../../app/theme";
import DashboardPanel from "./DashboardPanel";

export interface LessonHistoryChartViewProps {
    historyMap: LessonHistory
    ready: boolean
}

export default function LessonHistoryChartView({historyMap, ready} : LessonHistoryChartViewProps) {

    const today : Date = new Date();

    const allDates = useRef<Date[]>([]);
    const usedDates = useRef<Date[]>([]);

    const [dayLimit, setDayLimit] = useState<number>(14);
    const [oldestDate, setOldestDate] = useState<Date>(today);


    function applyTimeRange (dayRange : number) {
        usedDates.current = allDates.current.filter(d => ((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)) <= dayRange);
        const newOldest = new Date();
        newOldest.setDate(newOldest.getDate() - dayRange);
        setOldestDate(newOldest);
        setDayLimit(dayRange);
    }

    useEffect(() => {
        if (!ready)
            return;

        allDates.current = Array.from(historyMap.keys());
        applyTimeRange(14);
    }, [ready]);

    const seriesData = Array.from(historyMap.entries())
        .filter((kv) => new Date(kv[0]) >= oldestDate)
        .map((kv) => kv[1]);

    return (
        <DashboardPanel title="Completed Lessons" ready={ready && usedDates.current.length > 0} skeletonHeight={230}
                        titleAction={
                            <FormControl sx={{ minWidth: 60 }} size="small">
                                <Select size="small"
                                        value={dayLimit}
                                        onChange={e => applyTimeRange(e.target.value as number)}
                                        aria-label="Days range"
                                        MenuProps={{
                                            disableScrollLock: true,
                                            PaperProps: {sx: {borderRadius: 2}},
                                        }}
                                        sx={{
                                            color: theme.palette.secondary.contrastText,
                                            borderRadius: 2,
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none',
                                            },
                                            '& .MuiSvgIcon-root': {color: theme.palette.secondary.contrastText},
                                        }}
                                >
                                    <MenuItem value={7}>7 days</MenuItem>
                                    <MenuItem value={14}>14 days</MenuItem>
                                    <MenuItem value={30}>30 days</MenuItem>
                                </Select>
                            </FormControl>
                        }
        >
            <LineChart
                series={[{
                    data: seriesData,
                    valueFormatter: v => `${v} completed lessons`,
                    color: theme.palette.secondary.main,
                }]}
                xAxis={[{
                    scaleType: "time",
                    data: usedDates.current,
                    min: oldestDate,
                    max: today,
                    valueFormatter: (d, {location}) => location === 'tick' ? `${d.getDate()}.${d.getMonth() + 1}` : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`,
                }]}
                yAxis={[{
                    // Force a non-zero domain so the axis isn't hidden when every visible value is 0.
                    min: 0,
                    max: Math.max(1, ...seriesData),
                }]}
                height={200}
            />
        </DashboardPanel>
    );
}
